import type { Message } from '../types';
import { projectCachedMessage, type CachedMessage } from './message-cache';

export type MessageReceipt =
  | { type: 'new' | 'edit' | 'ack'; message: Message }
  | { type: 'delete'; messageId: string; deletedAt: number }
  | { type: 'reactions'; messageId: string; reactions: NonNullable<Message['reactions']> };

export interface ReceiptBuffer {
  receipts: MessageReceipt[];
  overflowed: boolean;
}

export const MAX_MESSAGE_RECEIPTS = 512;

export const createReceiptBuffer = (): ReceiptBuffer => ({ receipts: [], overflowed: false });

// The row and its query enrichment have different wire contracts. A sparse ACK
// carries no attachment/reaction assertion, while an explicit empty list does.
const mutationSnapshot = (message: Message): string => JSON.stringify({
  id: message.id, channel_id: message.channel_id, sender_id: message.sender_id,
  content: message.content, content_type: message.content_type, type: message.type,
  reply_to_id: message.reply_to_id, created_at: message.created_at, edited_at: message.edited_at,
  deleted_at: message.deleted_at ?? null, quick_action: message.quick_action ?? null,
});

const enrichment = (message: Message) => {
  const projected = projectCachedMessage({ ...message, deleted_at: null });
  return {
    sender_name: projected?.sender_name, mentions: projected?.mentions,
    attachments: projected?.attachments, reactions: projected?.reactions,
  };
};

const snapshotsAgree = (left: Message, right: Message): boolean => {
  if (mutationSnapshot(left) !== mutationSnapshot(right)) return false;
  const leftMetadata = enrichment(left);
  const rightMetadata = enrichment(right);
  return (Object.keys(leftMetadata) as Array<keyof typeof leftMetadata>).every((key) =>
    leftMetadata[key] === undefined || rightMetadata[key] === undefined ||
    JSON.stringify(leftMetadata[key]) === JSON.stringify(rightMetadata[key]));
};

const mergeSnapshot = (existing: Message, incoming: Message): Message => ({
  ...existing, ...incoming,
  ...enrichment(existing),
  ...Object.fromEntries(Object.entries(enrichment(incoming)).filter(([, value]) => value !== undefined)),
  quick_action: incoming.quick_action,
});

export const appendReceipt = (buffer: ReceiptBuffer, receipt: MessageReceipt): void => {
  if (buffer.overflowed) return;
  if (receipt.type === 'new' || receipt.type === 'ack') {
    const previous = buffer.receipts.find((candidate) => (candidate.type === 'new' || candidate.type === 'ack') &&
      candidate.message.id === receipt.message.id && snapshotsAgree(candidate.message, receipt.message));
    if (previous?.type === 'new' || previous?.type === 'ack') {
      previous.message = mergeSnapshot(previous.message, receipt.message);
      if (receipt.type === 'new') previous.type = 'new';
      return;
    }
  }
  if (buffer.receipts.length === MAX_MESSAGE_RECEIPTS) {
    buffer.receipts = [];
    buffer.overflowed = true;
    return;
  }
  buffer.receipts.push(structuredClone(receipt));
};

export interface ReconciledMessageWindow {
  messages: Message[];
  canonicalMessages: CachedMessage[];
  ambiguous: boolean;
}

export const reconcileMessageWindow = (
  messages: readonly Message[],
  receipts: readonly MessageReceipt[],
): ReconciledMessageWindow => {
  const rows = new Map(messages.map((message) => [message.id, message]));
  const knownIds = new Set(rows.keys());
  const deleted = new Map<string, number>();
  for (const message of messages) {
    if (message.deleted_at) deleted.set(message.id, message.deleted_at);
  }
  for (const receipt of receipts) {
    if (receipt.type === 'new') knownIds.add(receipt.message.id);
  }
  let ambiguous = false;
  for (const receipt of receipts) {
    if (receipt.type === 'ack') {
      const existing = rows.get(receipt.message.id);
      // An ACK confirms a send attempt, not continued membership in this window.
      // Only history or a creation event can establish a row after a deletion/read.
      if (!existing) {
        const creation = receipts.find((candidate) => candidate.type === 'new' && candidate.message.id === receipt.message.id);
        if (creation?.type === 'new' && !snapshotsAgree(creation.message, receipt.message)) ambiguous = true;
        continue;
      }
      if (!snapshotsAgree(existing, receipt.message)) { ambiguous = true; continue; }
      rows.set(receipt.message.id, mergeSnapshot(existing, receipt.message));
    } else if (receipt.type === 'new' || receipt.type === 'edit') {
      const existing = rows.get(receipt.message.id);
      if (existing && !snapshotsAgree(existing, receipt.message)) ambiguous = true;
      if (receipt.type === 'edit' && !existing) continue;
      const merged = existing ? mergeSnapshot(existing, receipt.message) : receipt.message;
      const deletedAt = deleted.get(receipt.message.id);
      if (deletedAt !== undefined) {
        if (!receipt.message.deleted_at) ambiguous = true;
        rows.set(receipt.message.id, { ...merged, content: '', deleted_at: deletedAt });
      } else {
        rows.set(receipt.message.id, merged);
        if (receipt.message.deleted_at) deleted.set(receipt.message.id, receipt.message.deleted_at);
      }
    } else if (receipt.type === 'delete') {
      deleted.set(receipt.messageId, receipt.deletedAt);
      const existing = rows.get(receipt.messageId);
      if (existing) {
        if (!existing.deleted_at) ambiguous = true;
        rows.set(receipt.messageId, { ...existing, content: '', deleted_at: receipt.deletedAt });
      }
    } else if (receipt.type === 'reactions' && knownIds.has(receipt.messageId)) {
      // Reactions have no revision, including when the message arrives later in this read.
      ambiguous = true;
      const existing = rows.get(receipt.messageId);
      if (existing) rows.set(receipt.messageId, { ...existing, reactions: receipt.reactions });
    }
  }
  const reconciled = [...rows.values()].sort((a, b) =>
    a.created_at - b.created_at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return {
    messages: reconciled,
    canonicalMessages: reconciled.flatMap((message) => {
      const projected = projectCachedMessage(message);
      return projected ? [projected] : [];
    }),
    ambiguous,
  };
};
